import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import SiteLayout from '@/components/feature/SiteLayout'
import MovieCard from '@/components/feature/MovieCard'
import { usePageSeo } from '@/hooks/usePageSeo'
import { categories, movies, years } from '@/mocks/movies'

const hotKeywords = ['悬疑', '爱情', '2024', '古装', '短片']

export default function Search() {
  const [params, setParams] = useSearchParams()
  const query = (params.get('q') ?? '').trim()
  const type = params.get('type') ?? 'all'
  const year = params.get('year') ?? 'all'

  const [keyword, setKeyword] = useState(query)

  useEffect(() => {
    setKeyword(query)
  }, [query])

  usePageSeo(
    query ? `搜索“${query}” - 麻豆传媒在线` : '搜索影视作品 - 麻豆传媒在线',
    query
      ? `在麻豆传媒在线搜索“${query}”的相关影视作品，支持按分类与上映年份筛选，快速找到想看的电影、电视剧与短片。`
      : '麻豆传媒在线搜索页面，支持按作品标题、题材类型、导演与演员关键词模糊搜索，并可结合分类与年份筛选。',
    '麻豆传媒在线搜索,影视搜索,影片查找,影视作品搜索,在线影视',
  )

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === 'all') {
        next.delete(key)
      } else {
        next.set(key, value)
      }
    })
    setParams(next)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const value = keyword.trim()
    const next = new URLSearchParams(params)
    if (value) {
      next.set('q', value)
    } else {
      next.delete('q')
    }
    setParams(next)
  }

  const keywordPool = query.toLowerCase()

  const results = movies
    .filter((movie) => {
      if (!keywordPool) return false
      const haystack = [
        movie.title,
        movie.director,
        ...movie.cast,
        ...movie.genres,
        String(movie.year),
        movie.tagline,
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(keywordPool)
    })
    .filter((movie) => (type === 'all' ? true : movie.category === type))
    .filter((movie) => (year === 'all' ? true : String(movie.year) === year))
    .sort((a, b) => a.hot - b.hot)

  const hotList = [...movies].sort((a, b) => a.hot - b.hot).slice(0, 4)

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="page-shell py-8 md:py-10">
          <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-800">搜索</span>
          </nav>

          <h1 className="mt-4 text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            搜索影视作品
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            输入作品标题、题材类型、导演或演员姓名，即可即时匹配站内示例作品。
          </p>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：第七封信、悬疑、顾行舟"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => setKeyword('')}
                  className="cursor-pointer text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-circle-fill text-sm" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-800 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-600" />
              热门搜索
            </span>
            {hotKeywords.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setKeyword(item)
                  updateParams({ q: item })
                }}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="page-shell py-8 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="shrink-0 text-xs text-foreground-500 sm:w-14">分类</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateParams({ type: 'all' })}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  type === 'all'
                    ? 'border-primary-800 bg-primary-800 text-background-50'
                    : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
                }`}
              >
                全部
              </button>
              {categories.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => updateParams({ type: item.key })}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    type === item.key
                      ? 'border-primary-800 bg-primary-800 text-background-50'
                      : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  <i className={item.icon} />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="shrink-0 text-xs text-foreground-500 sm:w-14">年份</span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => updateParams({ year: 'all' })}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  year === 'all'
                    ? 'border-primary-800 bg-primary-800 text-background-50'
                    : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
                }`}
              >
                全部
              </button>
              {years.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => updateParams({ year: String(item) })}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 font-label text-xs tracking-wide transition-colors ${
                    year === String(item)
                      ? 'border-primary-800 bg-primary-800 text-background-50'
                      : 'border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-500">
          <p>
            {query ? (
              <>
                关键词“<span className="font-medium text-foreground-900">{query}</span>”共找到{' '}
                <span className="font-medium text-foreground-900">{results.length}</span> 部作品
              </>
            ) : (
              '尚未输入关键词，可先浏览下方的热门作品'
            )}
          </p>
          {(type !== 'all' || year !== 'all') && (
            <button
              type="button"
              onClick={() => updateParams({ type: 'all', year: 'all' })}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-foreground-500 hover:text-accent-700"
            >
              <i className="ri-refresh-line" />
              清除筛选
            </button>
          )}
        </div>

        {results.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-x-4 lg:grid-cols-4">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-6">
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h2 className="mt-4 text-base font-medium text-foreground-900">
                {query ? '没有找到匹配的作品' : '输入关键词开始搜索'}
              </h2>
              <p className="mt-2 max-w-sm text-xs leading-6 text-foreground-500">
                {query
                  ? '试试更换关键词，或放宽分类与年份筛选。也可以直接浏览下方的热门推荐。'
                  : '支持作品标题、题材类型、导演与演员的模糊匹配，例如搜索“悬疑”或“顾行舟”。'}
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/category"
                  className="whitespace-nowrap rounded-md bg-primary-800 px-5 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  浏览全部分类
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('')
                    updateParams({ q: '', type: 'all', year: 'all' })
                  }}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-800"
                >
                  清空搜索条件
                </button>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-accent-600" />
                热门推荐
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-x-4 lg:grid-cols-4">
                {hotList.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  )
}